// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';

import GoffyButton from '@/components/GoffyButton';
import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';

import { newPhotoId } from '../newPhotoId';
import { fromWebFiles, pickFromCamera, pickFromLibrary, type PickOutcome } from '../pickPhotos';
import {
  enqueuePhotoUpload,
  setPhotoUploadAuthToken,
  subscribeToPhotoUploads,
} from '../uploadQueue';

type PhotoPickerBarProps = {
  tourId: string;
  token: string | null | undefined;
  /** Called once the queue drains, so the gallery can pick up the new rows. */
  onUploadsSettled: () => void;
};

/**
 * Add-photo controls on the gallery screen: camera plus a multi-select library pick.
 *
 * This is the secondary capture surface — the hole screen header is where photos are normally taken.
 * What this adds is everything outside a round (clubhouse, prize-giving) and shots taken earlier
 * that are still in the camera roll.
 */
export default function PhotoPickerBar({ tourId, token, onUploadsSettled }: PhotoPickerBarProps) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [note, setNote] = useState<string | null>(null);
  const webInputRef = useRef<HTMLInputElement | null>(null);
  const webCameraInputRef = useRef<HTMLInputElement | null>(null);
  const previousPendingRef = useRef(0);

  useEffect(() => {
    setPhotoUploadAuthToken(token);
  }, [token]);

  useEffect(
    () =>
      subscribeToPhotoUploads((state) => {
        setPendingCount(state.pending.length);
        if (state.lastError) {
          setNote(t('tourPhotos.uploadFailed'));
        }
        // Refresh the list on the falling edge, when the last upload finishes.
        if (previousPendingRef.current > 0 && state.pending.length === 0) {
          onUploadsSettled();
        }
        previousPendingRef.current = state.pending.length;
      }),
    [onUploadsSettled, t],
  );

  const handleOutcome = useCallback(
    (outcome: PickOutcome) => {
      if (outcome.kind === 'permission-denied') {
        setNote(outcome.source === 'camera' ? t('tourPhotos.cameraDenied') : t('tourPhotos.libraryDenied'));
        return;
      }
      if (outcome.kind === 'rejected') {
        setNote(outcome.reason === 'too-large' ? t('tourPhotos.tooLarge') : t('tourPhotos.unsupportedType'));
        return;
      }
      if (outcome.kind === 'cancelled') {
        return;
      }

      setNote(null);
      outcome.photos.forEach((photo) => {
        enqueuePhotoUpload({
          id: newPhotoId(),
          tourId,
          // Photos added from the gallery are untagged: the player is no longer on a hole, and
          // guessing a round would be worse than leaving it unassigned. Captions and round tagging
          // come later via PATCH.
          roundId: null,
          holeNumber: null,
          caption: null,
          photo,
        });
      });
    },
    [t, tourId],
  );

  const runNativePick = useCallback(
    async (pick: () => Promise<PickOutcome>) => {
      if (busy) {
        return;
      }
      setBusy(true);
      try {
        handleOutcome(await pick());
      } finally {
        setBusy(false);
      }
    },
    [busy, handleOutcome],
  );

  const onWebFilesSelected = useCallback(
    (event: { target: HTMLInputElement }) => {
      const files = Array.from(event.target.files ?? []);
      // Reset so picking the same file twice in a row still fires a change event.
      event.target.value = '';
      if (files.length > 0) {
        handleOutcome(fromWebFiles(files));
      }
    },
    [handleOutcome],
  );

  const isWeb = Platform.OS === 'web';

  return (
    <View style={styles.container} testID="tour-photos-picker">
      <View style={styles.row}>
        <GoffyButton
          label={t('tourPhotos.captureAction')}
          onPress={() =>
            isWeb ? webCameraInputRef.current?.click() : void runNativePick(pickFromCamera)
          }
          disabled={busy}
          size="compact"
          tone="accent"
          testID="tour-photos-capture"
        />
        <GoffyButton
          label={t('tourPhotos.pickAction')}
          onPress={() => (isWeb ? webInputRef.current?.click() : void runNativePick(pickFromLibrary))}
          disabled={busy}
          size="compact"
          tone="dark"
          testID="tour-photos-pick"
        />
      </View>

      {pendingCount > 0 ? (
        <Text style={styles.status} testID="tour-photos-uploading">
          {t('tourPhotos.uploading', { count: pendingCount })}
        </Text>
      ) : null}
      {note ? (
        <Text style={styles.error} testID="tour-photos-picker-error">
          {note}
        </Text>
      ) : null}

      {/* Web only. `launchImageLibraryAsync` is a silent no-op in the browser, so the file input is
          the real picker here — and `capture="environment"` gets the mobile-browser camera without
          rebuilding a getUserMedia preview. It is also what makes the flow testable with
          Playwright's setInputFiles. */}
      {isWeb ? (
        <>
          <input
            ref={webInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={onWebFilesSelected as unknown as React.ChangeEventHandler<HTMLInputElement>}
            style={{ display: 'none' }}
            data-testid="tour-photos-file-input"
          />
          <input
            ref={webCameraInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            onChange={onWebFilesSelected as unknown as React.ChangeEventHandler<HTMLInputElement>}
            style={{ display: 'none' }}
            data-testid="tour-photos-camera-input"
          />
        </>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 6,
  },
  row: {
    flexDirection: 'row',
    gap: 8,
  },
  status: {
    color: appColors.text.muted,
    fontSize: 13,
  },
  error: {
    color: appColors.status.warning,
    fontSize: 13,
  },
});
